import { useCallback, useState } from 'react';
import { AuthDeliveryChannel } from '@shared/enums/auth-delivery-channel.enum';

export interface DeliveryState {
  deliveredVia: AuthDeliveryChannel | null;
  availableChannels: AuthDeliveryChannel[];
}

export interface PhoneCodeDelivery extends DeliveryState {
  /** Store the channel a code was delivered through + the offered alternatives. */
  apply: (result: DeliveryState) => void;
  /** Clear delivery state (e.g. when leaving the verification step). */
  reset: () => void;
}

/**
 * Owns the `deliveredVia` / `availableChannels` pair that the auth-code
 * verification screen needs, so pages don't manage two correlated useStates by
 * hand. Reusable by any flow that sends a phone code (sign-in, sign-up).
 */
export function usePhoneCodeDelivery(): PhoneCodeDelivery {
  const [deliveredVia, setDeliveredVia] = useState<AuthDeliveryChannel | null>(
    null,
  );
  const [availableChannels, setAvailableChannels] = useState<
    AuthDeliveryChannel[]
  >([]);

  const apply = useCallback((result: DeliveryState) => {
    setDeliveredVia(result.deliveredVia);
    setAvailableChannels(result.availableChannels);
  }, []);

  const reset = useCallback(() => {
    setDeliveredVia(null);
    setAvailableChannels([]);
  }, []);

  return { deliveredVia, availableChannels, apply, reset };
}
